// One gallery cell: the full 240² BMP rendered as-is from its presigned R2 URL
// (no thumbnails — ADR-0022), lazy-loaded, with a per-item download.
//
// The <img> points straight at `item.url` (the short-TTL presigned R2 GET), so the
// browser fetches the bytes directly from R2 — never through the Worker. `loading`
// is "lazy" so off-screen items don't fetch until scrolled near (native lazy-load).

import { useState } from "react";
import type { MediaItem } from "../api";
import { downloadItem, filenameFor, mediaLabel } from "../download";
import { isClip, type SheetCores } from "../cores";
import { formatAbsolute, formatRelative } from "../format";
import { ContactSheet } from "./ContactSheet";

export interface MediaCardProps {
  item: MediaItem;
  /** Override the download flow (tests inject a stub; default hits the network). */
  onDownload?: (item: MediaItem) => Promise<void>;
  /** Override the Sheet decode/render seam (tests inject a stub; default uses the cores). */
  sheetCores?: SheetCores;
  /**
   * Open the detail lightbox for this card. The GRID owns the lightbox now (one shared
   * <MediaLightbox> so it can navigate to siblings), so the card just requests an open —
   * it no longer renders its own. Absent ⇒ the open affordance still shows but is inert.
   */
  onOpen?: () => void;
  /**
   * Mosaic-picker mode (B4). When `picking`, a tappable badge overlays the card —
   * `pickPosition` is the 1-based cell number (0 = unpicked) and `onPick` toggles
   * it. Off by default, so the card behaves exactly as in B3.
   */
  picking?: boolean;
  pickPosition?: number;
  onPick?: (item: MediaItem) => void;
  /**
   * Select mode (zip download). When `selecting`, the WHOLE card is the toggle (big
   * click target, not just the badge); `selected` is its state and `onSelect` toggles
   * it. `range` is true on a shift-click (the grid extends from its anchor). Mutually
   * exclusive with `picking` (the grid only ever turns one on). Off by default.
   */
  selecting?: boolean;
  selected?: boolean;
  onSelect?: (item: MediaItem, opts: { range: boolean }) => void;
  /**
   * Re-fetch fresh presigned URLs (a gallery reload) when this tile's short-TTL R2
   * URL has expired on a long-open tab and the <img> errors (#136). Without it an
   * expired tile silently shows the broken-image glyph.
   */
  onReload?: () => void;
  /**
   * Media Archive (ADR-0058). `archivedView` flips the action from Archive → Restore. `onArchive`
   * hides the card; `onRestore` un-hides it. For a CORRUPT (0-byte) card — whose other actions are
   * suppressed — Archive is the ONE action shown (the non-destructive way to retire a husk).
   * `mutating` disables the action while a batch is in flight.
   */
  archivedView?: boolean;
  onArchive?: (item: MediaItem) => void;
  onRestore?: (item: MediaItem) => void;
  mutating?: boolean;
}

export function MediaCard({
  item,
  onDownload,
  sheetCores,
  onOpen,
  picking = false,
  pickPosition = 0,
  onPick,
  selecting = false,
  selected = false,
  onSelect,
  onReload,
  archivedView = false,
  onArchive,
  onRestore,
  mutating = false,
}: MediaCardProps): React.JSX.Element {
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const name = filenameFor(item);
  const label = item.label ?? mediaLabel(item); // "Capture #7" / "Clip #3" — explicit label wins, else derived from the key
  const clip = isClip(item.key);
  // A 0-byte object is a corrupt Capture/Clip — a power sag at the SD write's onset saved
  // an empty file in the field. There are no bytes to render, so the tile shows a labeled
  // "unavailable" placeholder instead of an <img>, which also means the onError tap-to-refresh
  // (a false promise of recovery here) can never engage.
  const corrupt = item.size === 0;
  const picked = pickPosition > 0;
  const highlighted = picked || (selecting && selected);
  // Outside the Mosaic-pick / Select modes, the thumbnail opens a large detail view.
  const canOpen = !selecting && !picking;
  // The Archive/Restore action (ADR-0058): Restore in the Archived view, else Archive. Null when
  // the gallery wired no handler (offline mode / tests). For a corrupt card this is its ONE action.
  // ADR-0064 made Archive destructive on the CAMERA (the card copy is dropped on its next
  // Sync), so the control says so rather than letting "Archive" imply the old hide-only
  // behaviour. Restore is honest about being only half-reversible.
  const archiveAction =
    archivedView && onRestore
      ? {
          label: "Restore",
          hint: "Show in your gallery again. The camera copy was already removed and does not come back.",
          run: onRestore,
        }
      : !archivedView && onArchive
        ? {
            label: "Archive",
            hint: "Hide here, and remove it from the camera on its next Sync. The cloud copy is kept.",
            run: onArchive,
          }
        : null;

  async function handleDownload(): Promise<void> {
    setDownloading(true);
    setFailed(false);
    try {
      await (onDownload ?? downloadItem)(item);
    } catch {
      setFailed(true);
    } finally {
      setDownloading(false);
    }
  }

  // In BOTH multi-pick modes the whole card is the toggle — one big target, no text
  // drag — so Mosaic pick mode and ZIP select mode share a single tap grammar (#138).
  // Select mode adds a shift-click range; the numbered/check badge is a pure indicator.
  // Both multi-pick modes make the whole card a toggle sharing ONE keyboard handler; they
  // differ only in the pressed state, the label, and what activation does. Select carries the
  // shift-click range; the Mosaic label announces the assigned cell number (it's otherwise
  // only in an aria-hidden badge).
  // A corrupt card is never a toggle: its 0-byte object would only add a broken
  // entry to a ZIP or Mosaic, so Select/Mosaic-pick skip it entirely.
  const toggle = corrupt
    ? null
    : selecting
    ? {
        pressed: selected,
        label: selected ? `Deselect ${name}` : `Select ${name} for download`,
        activate: (e: React.MouseEvent | React.KeyboardEvent) =>
          onSelect?.(item, { range: e.shiftKey }),
      }
    : picking
      ? {
          pressed: picked,
          label: picked
            ? `Remove ${name} from the Mosaic (cell ${pickPosition})`
            : `Add ${name} to the Mosaic`,
          activate: () => onPick?.(item),
        }
      : null;

  const toggleProps = toggle
    ? {
        role: "button" as const,
        tabIndex: 0,
        "aria-pressed": toggle.pressed,
        "aria-label": toggle.label,
        onClick: (e: React.MouseEvent) => toggle.activate(e),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle.activate(e);
          }
        },
      }
    : {};

  return (
    <figure
      className={
        "media-card" +
        (highlighted ? " is-picked" : "") +
        (selecting || picking ? " is-selecting" : "")
      }
      data-testid="media-card"
      data-key={item.key}
      {...toggleProps}
    >
      {corrupt ? (
        // A 0-byte object can't be shown or recovered — render a permanent, non-interactive
        // "corrupt" placeholder in place of the <img> (so the tap-to-refresh path never fires
        // for it), announced as a single labeled image node for assistive tech.
        <div
          className="media-card__corrupt"
          data-testid="tile-corrupt"
          role="img"
          aria-label={`${name} is corrupt — unavailable`}
        >
          <span className="media-card__corrupt-mark" aria-hidden="true">
            ⚠
          </span>
          <span className="media-card__corrupt-label">Corrupt — unavailable</span>
        </div>
      ) : imgError ? (
        // The short-TTL presigned URL expired on a long-open tab (#136): show a 1:1
        // skeleton + a tap-to-refresh that re-lists the gallery for fresh URLs, instead
        // of the silent broken-image glyph the card had no onError for.
        <button
          type="button"
          className="media-card__reload"
          data-testid="tile-reload"
          aria-label={`${name} couldn’t load — tap to refresh`}
          onClick={() => {
            setImgError(false);
            onReload?.();
          }}
        >
          <span className="media-card__reload-label">↻ Tap to refresh</span>
        </button>
      ) : (
        (() => {
          const img = (
            <img
              className="media-card__img"
              src={item.url}
              alt={name}
              width={240}
              height={240}
              loading="lazy"
              decoding="async"
              onError={() => setImgError(true)}
              // Load the thumbnail in CORS mode (the bucket allows our origin — DEPLOY
              // step 2b / ADR-0022). Without this the browser caches a *non-CORS*
              // response for this presigned URL, and the contact-Sheet / Mosaic / Download
              // `fetch()` of the SAME url reuses that cached response → no ACAO → the
              // read is CORS-blocked ("Could not read this clip."). Matching the modes
              // also lets the Sheet reuse the bytes the gallery already fetched.
              crossOrigin="anonymous"
            />
          );
          // In normal mode the thumbnail is a button that opens the detail view; in
          // select/pick mode the card itself is the toggle, so it stays a plain <img>.
          return canOpen ? (
            <button
              type="button"
              className="media-card__open"
              onClick={() => onOpen?.()}
              aria-label={`Open ${name}`}
              title="View larger"
            >
              {img}
            </button>
          ) : (
            img
          );
        })()
      )}
      {picking && !corrupt && (
        // A pure indicator — the whole card is the toggle now (clicks fall through to the
        // figure handler), exactly like the select badge.
        <span
          className={"media-card__pick media-card__pick--ind" + (picked ? " is-on" : "")}
          data-testid="pick-badge"
          aria-hidden="true"
        >
          {picked ? pickPosition : "+"}
        </span>
      )}
      {selecting && !corrupt && (
        // A pure indicator — the whole card is the clickable toggle (pointer-events
        // are off so clicks fall through to the figure's handler).
        <span
          className={"media-card__pick media-card__pick--ind" + (selected ? " is-on" : "")}
          data-testid="select-badge"
          aria-hidden="true"
        >
          {selected ? "✓" : "+"}
        </span>
      )}
      <figcaption className="media-card__foot">
        <span className="media-card__name" title={name}>
          {label}
        </span>
        {/* When the camera synced this to the cloud — the camera has no RTC, so this
            "Added" time is the honest timestamp, not capture time (ADR-0022). */}
        <time
          className="media-card__date"
          dateTime={new Date(item.uploaded).toISOString()}
          title={`Added ${formatAbsolute(item.uploaded)}`}
          data-testid="media-date"
        >
          {formatRelative(item.uploaded)}
        </time>
        {/* The Clip-only Contact sheet lives as a compact grid-icon button in the empty
            space beside the name/date — the two-line text button made Clip cards taller
            than Capture cards (the actions row wrapped). */}
        {!selecting && !picking && !corrupt && clip && (
          <button
            type="button"
            className="media-card__sheet"
            onClick={() => setSheetOpen(true)}
            aria-label={`Contact sheet for ${name}`}
            title="Contact sheet"
          >
            {/* An inline 2×2 grid (the ▦ glyph rendered as an unreadable smudge at 16px). */}
            <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor" aria-hidden="true">
              <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1" />
              <rect x="9" y="1.5" width="5.5" height="5.5" rx="1" />
              <rect x="1.5" y="9" width="5.5" height="5.5" rx="1" />
              <rect x="9" y="9" width="5.5" height="5.5" rx="1" />
            </svg>
          </button>
        )}
        {/* Per-item actions are hidden in EITHER multi-pick mode (the card is one toggle). The
            byte-reading actions (Contact sheet, Download) are also hidden for a corrupt (0-byte)
            object — but Archive/Restore stays: it's the ONE action a corrupt card exposes, the
            non-destructive way to retire a husk (ADR-0058). */}
        {!selecting && !picking && (!corrupt || archiveAction) && (
          <span className="media-card__actions">
            {!corrupt && (
              <button
                type="button"
                className="media-card__download"
                onClick={handleDownload}
                disabled={downloading}
                aria-label={`Download ${name}`}
              >
                {downloading ? "…" : failed ? "Retry" : "Download"}
              </button>
            )}
            {archiveAction && (
              <button
                type="button"
                className="media-card__archive"
                data-testid="card-archive"
                onClick={() => archiveAction.run(item)}
                disabled={mutating}
                title={archiveAction.hint}
                aria-label={`${archiveAction.label} ${name} — ${archiveAction.hint}`}
              >
                {archiveAction.label}
              </button>
            )}
          </span>
        )}
      </figcaption>
      {sheetOpen && (
        <ContactSheet
          item={item}
          onClose={() => setSheetOpen(false)}
          sheetCores={sheetCores}
        />
      )}
    </figure>
  );
}
